import { DataGrid, type GridColumn } from "@/components/data-grid";
import { useMemo } from "react";

import { formatCell } from "../dataset.ts";
import { CHAT_TEXTS as TEXTS } from "../texts.ts";

/**
 * Resultado suelto de un análisis: un diccionario, una lista o un número.
 *
 * Existe porque `run_analysis` no siempre devuelve una tabla. Cuando devuelve
 * `{"OK": 4094, "DUPLICADO": 78}`, dibujar una tabla vacía era el error; esto
 * lo muestra como lo que es, y con las cifras viniendo del cálculo y no del
 * texto del modelo (sección 9).
 */
interface Props {
  props: Record<string, unknown>;
}

export function KeyValues({ props }: Props) {
  /**
   * `value` existe porque `run_analysis` envuelve su resultado, pero eso era
   * una particularidad suya, no un contrato. Sin envoltorio, lo correcto es
   * pintar las props tal cual: un componente de clave/valor sabe hacerlo.
   *
   * Antes devolvia "El análisis no devolvió nada" para toda herramienta que no
   * usara ese envoltorio, que eran seis.
   */
  const value = "value" in props ? props["value"] : props;

  if (value === null || value === undefined) {
    return (
      <p className="text-sm text-muted-foreground">{TEXTS.value.nothing}</p>
    );
  }

  if (typeof value !== "object") {
    // Una cifra sola es el resultado: se lee de lejos y sin buscarla.
    return (
      <p className="m-0 text-3xl font-semibold tracking-tight tabular-nums">
        {formatCell(value as string | number | boolean, "number")}
      </p>
    );
  }

  if (Array.isArray(value)) {
    // `Array.isArray` sobre `unknown` deja los elementos en `any`; se acota.
    const items = value as unknown[];
    const first = items[0];

    // Una lista de objetos se lee mucho mejor como tabla improvisada.
    if (typeof first === "object" && first !== null && !Array.isArray(first)) {
      return <ObjectList rows={items as Record<string, unknown>[]} />;
    }

    return (
      <ul className="m-0 list-disc pl-5">
        {items.slice(0, 50).map((item, index) => (
          <li key={index}>{render(item)}</li>
        ))}
      </ul>
    );
  }

  const entries = Object.entries(value as Record<string, unknown>);
  return (
    // El hueco de 1px sobre el color del borde hace de línea entre filas sin
    // tener que decidir cuál de las dos la lleva.
    <dl className="m-0 grid gap-px overflow-hidden rounded-md border bg-border">
      {entries.slice(0, 60).map(([key, entry]) => (
        <div
          key={key}
          className="flex items-baseline gap-4 bg-card px-3.5 py-1.5"
        >
          <dt className="text-sm text-muted-foreground">{key}</dt>
          <dd className="m-0 ml-auto font-medium tabular-nums">
            {render(entry)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function ObjectList({ rows }: { rows: Record<string, unknown>[] }) {
  /**
   * Las columnas salen de TODAS las filas y no solo de la primera: una fila
   * puede traer un campo que otra no tiene. Se memorizan porque la rejilla
   * reconstruye la tabla entera si cambian de identidad.
   */
  const columns = useMemo(() => {
    const keys = [...new Set(rows.flatMap((row) => Object.keys(row)))];
    return keys.map((key): GridColumn<Record<string, unknown>> => ({
      key,
      label: key,
      numeric: rows.some((row) => typeof row[key] === "number"),
      render: (row) => render(row[key]),
      sortValue: (row) => {
        const cell = row[key];
        return typeof cell === "number" ||
          typeof cell === "string" ||
          typeof cell === "boolean"
          ? cell
          : null;
      },
    }));
  }, [rows]);

  const shown = useMemo(() => rows.slice(0, 100), [rows]);

  return (
    <div className="scroll-quiet max-h-104 overflow-auto rounded-lg border">
      <DataGrid columns={columns} rows={shown} />
    </div>
  );
}

const render = (value: unknown): string => {
  if (value === null || value === undefined) return "—";
  if (typeof value === "number") return value.toLocaleString("es");
  if (typeof value === "boolean")
    return value ? TEXTS.value.yes : TEXTS.value.no;
  if (typeof value === "string") return value;
  return JSON.stringify(value);
};
